/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { type AxiosInstance } from 'axios';
import { requestBaseURLGCPExtOrInt } from '@/config/serviceAPI';
import { getAxios } from '@/lib/axios';
import { createQuery } from '@/utils/createReactQuery';
import { useLocale } from '@/hooks/useLocale';
import { getCountryFromLocale } from '@/utils/getCountryFromLocale';
import {
  type GetSeoMetadataUsingGetParamsModel,
  type SeoMetadataResponseModel,
} from '@/api/types/seo-data-types';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { parseUrl } from '@/utils/urlHelpers';
import { useAppState } from '@/stores/AppState';
import { showXMPreviewDate } from '@/utils/showXMPreviewDate';
import { type QueryClient } from '@tanstack/react-query';
import { usePageType } from '@/features/category/api/getPageType';
import { type SeoMetadataInfo, type SeoMetadataOptions } from '../interface';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { isServer } from '@/utils/isServer';
import environmentConfig from '@/config/environment';

const URL = `${requestBaseURLGCPExtOrInt}/sls/b2c/product-discovery-seo-data-bs/v3/seo-metadata`;

export const getSeoMetadataKey = () => ['seoMetadata'] as const;

const allowedRoutes = new Set(['/[...seoUrlPath]', '/products/reviews/[...slug]']);

const getSeoMetadataInfo = async (
  options: SeoMetadataOptions,
  axiosInstance?: AxiosInstance,
): Promise<SeoMetadataInfo> => {
  const {
    botEnabledFacetPath,
    country,
    customerType,
    preview,
    seoUrl,
    vehicleMake,
    vehicleModel,
    vehicleYear,
    pageType,
    firstFacetValue,
    makeModelYearPath,
    shouldCallACES,
  } = options;

  const params: GetSeoMetadataUsingGetParamsModel = {
    botEnabledFacetPath,
    country,
    customerType,
    preview,
    seoUrl,
    vehicleMake,
    vehicleModel,
    vehicleYear,
    pageType,
    firstFacetValue,
    ...(shouldCallACES ? { makeModelYearPath } : {}),
  };

  const response = await getAxios(axiosInstance).get<SeoMetadataResponseModel>(URL, {
    params,
    withCredentials: !environmentConfig.DISABLE_GCP_API_CREDENTIALS,
  });

  return selector(response.data);
};

const selector = (data: SeoMetadataResponseModel): SeoMetadataInfo => {
  return data;
};

const { useData: useSeoMetadataInfo, prefetch: prefetchSeoMetadataInfo } = createQuery<
  SeoMetadataInfo,
  SeoMetadataOptions
>('seoMetadata', async (options, axiosInstance) =>
  getSeoMetadataInfo({ ...options }, axiosInstance),
);

export const prefetchSeoMetadataInfoResults = async (
  axiosInstance: AxiosInstance,
  queryClient: QueryClient,
  options: SeoMetadataOptions,
) => {
  return prefetchSeoMetadataInfo(queryClient, options, axiosInstance);
};

type UseSEOMetadataOptions = Omit<SeoMetadataOptions, 'shouldCallACES'> & {
  selectedFacetValue?: string;
  enabled?: boolean;
};

export const useSeoMetadata = ({
  enabled = true,
  selectedFacetValue,
  ...options
}: UseSEOMetadataOptions) => {
  const locale = useLocale();
  const country = getCountryFromLocale(locale);
  const customerType = 'B2C';
  const router = useLegacyRouter();
  const appState = useAppState();
  const { acesEnabled } = useAcesFlags();

  // TODO: replace with router.asPath when Next bug is fixed
  // https://github.com/vercel/next.js/issues/46876
  const reqUrl = isServer ? appState.state.reqUrl : router.asPath;

  if (reqUrl == null) {
    throw new Error(`A 'path' option is required`);
  }

  const parsedUrl = parseUrl(reqUrl).url;
  const isReviewsUrl = router.route === '/products/reviews/[...slug]';

  const {
    data: pageTypeData,
    refetch: refetchPageData,
    isFetching: isPageTypeFetching,
  } = usePageType({ enabled });

  if (pageTypeData && !pageTypeData.pageType && !isPageTypeFetching && enabled) {
    void refetchPageData();
  }

  const botEnabledFacetPath = pageTypeData?.botEnabledFacetPath ?? undefined;

  const seoUrl = pageTypeData?.canonicalPath;
  const pageType = isReviewsUrl ? 'ProductReview' : pageTypeData?.subPageType;
  const makeModelYearPath = pageTypeData?.makeModelYearPath;

  return useSeoMetadataInfo({
    ...options,
    enabled: enabled && (!!seoUrl || !!parsedUrl) && allowedRoutes.has(router.route),
    botEnabledFacetPath: options.botEnabledFacetPath ?? botEnabledFacetPath,
    country: options.country ?? country,
    customerType: options.customerType ?? customerType,
    preview: options.preview ?? showXMPreviewDate(),
    seoUrl: seoUrl ?? parsedUrl,
    pageType,
    firstFacetValue: selectedFacetValue || undefined,
    makeModelYearPath: options.makeModelYearPath ?? makeModelYearPath,
    shouldCallACES: acesEnabled,
  });
};
